import { useNavigate } from 'react-router-dom'
import { ArrowLeft, AlertTriangle, Pill } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useActivePatient } from '@/context/ActivePatientContext'
import { useClinicalSummary } from '@/hooks/useClinicalSummary'
import { getInitials, calculateAge } from '@/lib/utils'

interface PatientHeaderStripProps {
  onEditClick?: () => void
}

export function PatientHeaderStrip({ onEditClick }: PatientHeaderStripProps) {
  const navigate = useNavigate()
  const { activePatientData, clearActivePatient } = useActivePatient()
  const { data: summary } = useClinicalSummary(activePatientData?.id)

  if (!activePatientData) return null

  const patient = activePatientData
  const initials = getInitials(patient.first_name, patient.last_name)

  const genderLabels: Record<string, string> = {
    male: 'M',
    female: 'F',
    non_binary: 'No binario',
    prefer_not_to_say: 'N/E',
    other: 'Otro',
  }
  const sex = genderLabels[patient.gender] ?? '—'

  const handleBack = () => {
    clearActivePatient()
    navigate('/patients')
  }

  const allergyCount = summary?.allergies.length ?? 0
  const medicationCount = summary?.active_medications.length ?? 0
  const hasSevereAllergy = summary?.allergies.some((a) => a.severity === 'severe')

  return (
    <div className="bg-ml-surface border-b border-ml-outline-variant/10 px-6 lg:px-12 py-3">
      <div className="flex items-center gap-4">
        {/* Back button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBack}
          className="text-ml-on-surface-variant hover:text-ml-primary -ml-2 h-8 w-8 p-0"
          title="Volver a pacientes"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>

        {/* Patient photo */}
        {patient.photo_url ? (
          <img
            src={patient.photo_url}
            alt={patient.full_name}
            className="h-10 w-10 rounded-full object-cover border-2 border-ml-outline-variant/20 flex-shrink-0"
          />
        ) : (
          <div className="h-10 w-10 rounded-full bg-ml-primary flex items-center justify-center text-white text-sm font-headline italic border-2 border-ml-outline-variant/20 flex-shrink-0">
            {initials}
          </div>
        )}

        {/* Name + MRN + identity (NOM-004 6.1.1 identificación) */}
        <div className="flex flex-col min-w-0">
          <h2 className="text-sm font-headline italic text-ml-primary leading-tight truncate max-w-[240px]">
            {patient.full_name}
          </h2>
          <span className="text-[11px] text-ml-on-surface-variant/60 font-mono tracking-wider">
            Exp. {patient.mrn || patient.id.slice(0, 8)}
            {' · '}Sexo {sex}
            {patient.date_of_birth ? ` · ${calculateAge(patient.date_of_birth)} años` : ''}
            {patient.curp ? ` · CURP ${patient.curp}` : ''}
          </span>
        </div>

        {/* Clinical summary indicators (REQ-SUM-03) */}
        {summary && (
          <div className="hidden sm:flex items-center gap-2 ml-2">
            {allergyCount > 0 && (
              <button
                type="button"
                onClick={() => navigate(`/workspace/patient/${patient.id}/diagnostico`)}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-widest border ${
                  hasSevereAllergy
                    ? 'bg-ml-error/10 text-ml-error border-ml-error/20'
                    : 'bg-ml-surface-container-high text-ml-on-surface-variant border-ml-outline-variant/20'
                }`}
              >
                <AlertTriangle className="h-3 w-3" />
                {allergyCount} alergia{allergyCount === 1 ? '' : 's'}
              </button>
            )}
            {medicationCount > 0 && (
              <button
                type="button"
                onClick={() => navigate(`/workspace/patient/${patient.id}/diagnostico`)}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-ml-surface-container-high text-ml-on-surface-variant border border-ml-outline-variant/20 text-[10px] font-bold uppercase tracking-widest"
              >
                <Pill className="h-3 w-3" />
                {medicationCount} medicamento{medicationCount === 1 ? '' : 's'}
              </button>
            )}
          </div>
        )}

        {/* Status chip */}
        {patient.current_status && (
          <span className="flex-shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-sm bg-ml-surface-container-high text-[10px] font-bold uppercase tracking-widest text-ml-primary border border-ml-outline-variant/20">
            {patient.current_status}
          </span>
        )}

        {/* Edit button (optional, triggers parent dialog) */}
        {onEditClick && (
          <button
            onClick={onEditClick}
            className="ml-auto flex-shrink-0 text-xs text-ml-on-surface-variant hover:text-ml-primary transition-colors underline underline-offset-2"
          >
            Editar
          </button>
        )}
      </div>
    </div>
  )
}
